/**
 * Prepares filter parameters by converting array items to their IDs, date values to ISO format, and filtering out null values
 * @param {Object} value - The filter object to process (properties can be arrays, strings, or dates)
 * @returns {Object} Processed filter parameters
 */
export const prepareFilters = (value) => {
  if (!value || typeof value !== 'object') {
    return {}
  }

  const processValue = (val) => {
    if (val instanceof Date) {
      return getDateISO(val)
    }

    return val
  }

  return Object.fromEntries(
    Object.entries(value)
      .filter(([key, val]) => val != null && val.length !== 0)
      .map(([key, val]) => [key, processValue(val)]),
  )
}

/**
 * Smoothly scrolls to the top of the page
 */
export const scrollTop = () => {
  window.scrollTo({
    top: 0,
    behavior: 'smooth',
  })
}

/**
 * Formats a date string according to locale preferences
 * @param {string|Date} dateString - The date to format
 * @param {boolean} [time=false] - Whether to include time in the format
 * @param {string} [locale='ro-RO'] - The locale to use for formatting
 * @returns {string} Formatted date string
 */
export const formatDateLocale = (
  dateString,
  time = false,
  locale = 'ro-RO',
) => {
  if (!dateString) return ''

  try {
    const date = new Date(dateString)

    if (isNaN(date.getTime())) {
      throw new Error('Invalid date')
    }

    const defaultOptions = {
      year: 'numeric',
      month: 'numeric',
      day: 'numeric',
    }

    if (time) {
      defaultOptions.hour = '2-digit'
      defaultOptions.minute = '2-digit'
    }

    // JavaScript automatically converts timezone-aware ISO strings to user's local timezone
    // Backend sends format like: 2025-11-18T14:01:10.235884+02:00
    return date.toLocaleDateString(locale, defaultOptions).replaceAll('/', '.')
  } catch (error) {
    console.error('Error formatting date:', error)
    return String(dateString)
  }
}

/**
 * Formats a number as currency using Intl.NumberFormat
 * @param {number|string} amount - The amount to format
 * @param {string} [locale='ro-RO'] - The locale to use for formatting
 * @param {string} [currency='RON'] - The currency code
 * @returns {string} Formatted currency string
 */
export const formatCurrency = (amount, locale = 'ro-RO', currency = 'RON') => {
  const numericAmount = parseFloat(amount)

  if (isNaN(numericAmount)) return '-'

  try {
    return new Intl.NumberFormat(locale, {
      style: 'currency',
      currency: currency,
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(numericAmount)
  } catch (error) {
    console.error('Error formatting currency:', error)
    return `${numericAmount.toFixed(2)} ${currency}`
  }
}

/**
 * Gets the ISO date string (YYYY-MM-DD) from a Date object
 * @param {Date} date - The date object
 * @returns {string} ISO date string or empty string if invalid
 */
export const getDateISO = (date) => {
  if (!date || !(date instanceof Date) || isNaN(date.getTime())) {
    return null
  }

  try {
    const offsetDate = new Date(
      date.getTime() - date.getTimezoneOffset() * 60000,
    )
    return offsetDate.toISOString().split('T')[0]
  } catch (error) {
    console.error('Error getting ISO date:', error)
    return null
  }
}

/**
 * Gets the time string in HH:MM format from a Date object
 * @param {Date} date - The date object
 * @returns {string} Time string in HH:MM format or empty string if invalid
 */
export const getTimeISO = (date) => {
  if (!date || !(date instanceof Date) || isNaN(date.getTime())) {
    return null
  }

  try {
    return date.toTimeString().slice(0, 5) // More efficient than manual padding
  } catch (error) {
    console.error('Error getting time ISO:', error)
    return null
  }
}

/**
 * Scrolls an element into view with smooth behavior
 * @param {Element} element - The element to scroll to
 * @param {ScrollIntoViewOptions} [options={}] - Additional scroll options
 */
export const scrollTo = (element, options = {}) => {
  if (!element || typeof element.scrollIntoView !== 'function') {
    console.warn('Invalid element provided to scrollTo')
    return
  }

  element.scrollIntoView({ behavior: 'smooth', ...options })
}

/**
 * Formats a price with currency information
 * @param {Object} priceData - Price data object
 * @param {number|string} priceData.price - The price value
 * @param {Object} [priceData.currency] - Currency object with properties
 * @param {string} [fallbackCurrency='lei'] - Fallback currency string
 * @returns {string} Formatted price string
 */
export const formatPrice = ({ price, currency }, fallbackCurrency = 'lei') => {
  const numericPrice = parseFloat(price)

  if (isNaN(numericPrice)) {
    return '0.00 ' + fallbackCurrency
  }

  const currencyStr = currency ? currency.toLowerCase() : fallbackCurrency
  return `${numericPrice.toFixed(2)} ${currencyStr}`
}

/**
 * Checks if an object is empty or null/undefined
 * @param {any} obj - The object to check
 * @returns {boolean} True if object is empty, null, or undefined
 */
export const isEmpty = (obj) => {
  if (obj == null) return true
  if (typeof obj !== 'object') return false
  return Object.keys(obj).length === 0
}

/**
 * Returns the appropriate singular or plural form based on count
 * @param {number} count - The count to check
 * @param {string} singular - The singular form
 * @param {string} plural - The plural form
 * @returns {string} The appropriate form based on count
 */
export const pluralize = (count, singular, plural) => {
  if (typeof count !== 'number' || isNaN(count)) {
    return plural // Default to plural for safety
  }
  return Math.abs(count) === 1 ? singular : plural
}
